import { c as _c } from "react/compiler-runtime";
import { useToast } from '@common/hooks';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AuthApi, UnauthApi } from '@/api';
import { useStatusBarColor } from '@/hooks/useStatusBarColor';
import { useAuthStore } from '@/stores/AuthStore';
import type { DeviceTokenData } from '@/stores/DeviceStore';
import { STATUS_COLOR, useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { isAppVersionAtLeast } from '@/utils';
import { MESSAGE_KEYWORD } from '../_utils/reserved-keywords';
import { useHandleApp } from './useHandleApp';

//* 애드팝콘 SSP 광고 SDK·혜택탭 신규 기능 최소 요구 버전 — 미만이면 강제 업데이트
//! 스토어에 배포 완료된 버전만 올린다. forceUpdate 모달은 닫을 수 없어 미배포 버전을 넣으면 앱이 잠긴다.
const MIN_APP_VERSION = '3.2.0';
export const useMountAppData = () => {
  const $ = _c(43);
  const [osType, setAppVersion, tempDeviceTokenData, setTempDeviceTokenData, deviceTokenData, setDeviceTokenData, webviewType, setAppModal] = useDeviceInfoStore(useShallow(_temp));
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: MESSAGE_KEYWORD.VERSION
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    request: requestAppVersion,
    response: responseAppVersion
  } = useHandleApp(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      type: MESSAGE_KEYWORD.NOTIFICATION
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    request: requestNotification,
    response: responseNotification
  } = useHandleApp(t1);
  let t2;
  let t3;
  if ($[2] !== osType || $[3] !== requestAppVersion) {
    t2 = () => {
      if (!osType) {
        return;
      }
      requestAppVersion();
    };
    t3 = [requestAppVersion, osType];
    $[2] = osType;
    $[3] = requestAppVersion;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  const isFirstLoading = responseAppVersion?.isSuccess && responseAppVersion.data ? responseAppVersion.data.isFirstLoading : "Y";
  let t4;
  let t5;
  if ($[6] !== responseAppVersion || $[7] !== setAppModal || $[8] !== setAppVersion || $[9] !== webviewType) {
    t4 = () => {
      if (responseAppVersion?.isSuccess && responseAppVersion?.data) {
        const {
          version
        } = responseAppVersion.data;
        if (webviewType === WEBVIEW_TYPE.WELLO_APP && version && !isAppVersionAtLeast(version, MIN_APP_VERSION)) {
          setAppModal({
            type: "forceUpdate",
            isOpened: true
          });
        }
        setAppVersion(version);
      }
    };
    t5 = [responseAppVersion, setAppVersion, webviewType, setAppModal];
    $[6] = responseAppVersion;
    $[7] = setAppModal;
    $[8] = setAppVersion;
    $[9] = webviewType;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[12] !== deviceTokenData || $[13] !== isFirstLoading || $[14] !== osType || $[15] !== requestNotification) {
    t6 = () => {
      if (!osType) {
        return;
      }
      if (!deviceTokenData && isFirstLoading === "N") {
        requestNotification();
      }
    };
    t7 = [deviceTokenData, requestNotification, isFirstLoading, osType];
    $[12] = deviceTokenData;
    $[13] = isFirstLoading;
    $[14] = osType;
    $[15] = requestNotification;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  useEffect(t6, t7);
  const isLogin = useAuthStore(_temp2);
  const {
    toastError
  } = useToast();
  let t8;
  if ($[18] !== isLogin) {
    t8 = requestBody => isLogin ? AuthApi["post:/wello/v2/auth/wello-device-info"]({
      body: {
        ...requestBody
      }
    }) : UnauthApi["post:/wello/v2/allows/wello-device-info"]({
      body: {
        ...requestBody
      }
    });
    $[18] = isLogin;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== setDeviceTokenData || $[21] !== tempDeviceTokenData) {
    t9 = () => {
      if (tempDeviceTokenData) {
        setDeviceTokenData(tempDeviceTokenData);
      }
    };
    $[20] = setDeviceTokenData;
    $[21] = tempDeviceTokenData;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== toastError) {
    t10 = error => toastError({
      error
    });
    $[23] = toastError;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t8 || $[27] !== t9) {
    t11 = {
      mutationFn: t8,
      onSuccess: t9,
      onError: t10
    };
    $[25] = t10;
    $[26] = t8;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const {
    mutate: saveDeviceTokenMutate
  } = useMutation(t11);
  let t12;
  if ($[29] !== isLogin || $[30] !== responseNotification || $[31] !== setTempDeviceTokenData) {
    t12 = () => {
      if (!responseNotification || isLogin === undefined) {
        return;
      }
      const {
        isSuccess,
        data
      } = responseNotification;
      if (!isSuccess || !data) {
        return;
      }
      setTempDeviceTokenData(data);
    };
    $[29] = isLogin;
    $[30] = responseNotification;
    $[31] = setTempDeviceTokenData;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== isLogin || $[34] !== responseNotification || $[35] !== saveDeviceTokenMutate || $[36] !== setTempDeviceTokenData) {
    t13 = [isLogin, responseNotification, saveDeviceTokenMutate, setTempDeviceTokenData];
    $[33] = isLogin;
    $[34] = responseNotification;
    $[35] = saveDeviceTokenMutate;
    $[36] = setTempDeviceTokenData;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  useEffect(t12, t13);
  let t14;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      color: STATUS_COLOR.DEFAULT
    };
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  useStatusBarColor(t14);
  let t15;
  let t16;
  if ($[39] !== saveDeviceTokenMutate || $[40] !== tempDeviceTokenData) {
    t15 = () => {
      if (tempDeviceTokenData) {
        saveDeviceTokenMutate(tempDeviceTokenData);
      }
    };
    t16 = [saveDeviceTokenMutate, tempDeviceTokenData];
    $[39] = saveDeviceTokenMutate;
    $[40] = tempDeviceTokenData;
    $[41] = t15;
    $[42] = t16;
  } else {
    t15 = $[41];
    t16 = $[42];
  }
  useEffect(t15, t16);
};
function _temp(state) {
  return [state.osType, state.setAppVersion, state.tempDeviceTokenData, state.setTempDeviceTokenData, state.deviceTokenData, state.setDeviceTokenData, state.webviewType, state.setAppModal];
}
function _temp2(state_0) {
  return state_0.isLogin;
}